export const WEAPON_TYPES = [
  "great_sword",
  "long_sword",
  "sword_shield",
  "dual_blades",
  "hammer",
  "hunting_horn",
  "lance",
  "gunlance",
  "switch_axe",
  "charge_blade",
  "insect_glaive",
  "light_bowgun",
  "heavy_bowgun",
  "bow",
] as const;
export type WeaponType = (typeof WEAPON_TYPES)[number];
export type ArmorSlot = "head" | "chest" | "arms" | "waist" | "legs";
export type Resistance = {
  fire: number;
  water: number;
  thunder: number;
  ice: number;
  dragon: number;
};
export type ItemNumericMeta =
  | {
      kind: "weapon";
      weaponType: WeaponType;
      rarity: number;
      attack: number;
      affinity: number;
      element?: { type: string; value: number };
      sharpnessTier: number;
      skills: string[];
    }
  | {
      kind: "armor";
      slot: ArmorSlot;
      rarity: number;
      defense: number;
      resistances: Resistance;
      skills: string[];
    }
  | { kind: "charm"; rarity: number; skills: string[] }
  | {
      kind: "material";
      rarity: number;
      tags: MaterialTag[];
      monsterId?: string;
    }
  | { kind: "consumable"; effectProfile: string }
  | { kind: "ammo"; ammoType: string };
export type MaterialTag =
  | `monster:${string}`
  | `part:${string}`
  | `element:${string}`
  | `tier:${number}`
  | "bone"
  | "ore"
  | "plant"
  | "bug"
  | "fish"
  | "generic";
export interface ItemDefinition {
  key: string;
  name: string;
  description: string;
  appearance?: string;
  meta: ItemNumericMeta;
}
export interface AcquisitionLot {
  id: string;
  quantity: number;
  acquiredAt: number;
  source: string;
}
export interface ItemRegistryV1 {
  version: 1;
  definitions: Record<string, ItemDefinition>;
  lots: Record<string, AcquisitionLot[]>;
}
export interface EquipmentRef {
  itemKey: string;
  lotId?: string;
}
export type CharacterGender = "male" | "female" | "unspecified";
export type RaceId = "human" | "felyne" | "wyverian" | "monster";
export type ProfessionId =
  | "hunter"
  | "guild_staff"
  | "investigator"
  | "artisan"
  | "villager"
  | "researcher"
  | "caravan"
  | "custom";
export interface RaceIndices {
  health: number;
  stamina: number;
  dodge: number;
  attack: number;
}
export interface CharacterProfile {
  gender: CharacterGender;
  race: {
    id: Exclude<RaceId, "monster">;
    label: string;
    indices: RaceIndices;
  };
  profession: {
    id: ProfessionId;
    label: string;
    description: string;
    hasHunterLicense: boolean;
  };
  birthplace: { id: string; label: string; description: string };
  background: string;
  hook: string;
}
export interface PlayerCharacter {
  id: "B1";
  identity: { name: string; pronouns: string; origin: string };
  profile: CharacterProfile;
  hunter: { hr: number; hrExp: number };
  vitals: { health: number; stamina: number };
  wallet: { zenny: number; guildPoints: number };
  equipped: {
    weapon: EquipmentRef | null;
    head: EquipmentRef | null;
    chest: EquipmentRef | null;
    arms: EquipmentRef | null;
    waist: EquipmentRef | null;
    legs: EquipmentRef | null;
    charm: EquipmentRef | null;
  };
  proficiency: Record<WeaponType, number>;
  derived: { attack: number; defense: number; resistances: Resistance };
}
export type TaskStatus = "available" | "active" | "completed" | "failed";
export interface GameTask {
  id: string;
  source: "preset" | "generated";
  templateId?: string;
  title: string;
  description: string;
  questType: "hunt" | "capture" | "gather" | "deliver" | "special";
  star: number;
  targetMonsterIds: string[];
  targetMonsterNames: string[];
  location: string;
  contractFee: number;
  status: TaskStatus;
  reward: { zenny: number; hrExp: number; guildPoints: number };
  settlementDone: boolean;
}
export interface BoundedStat {
  current: number;
  min: number;
  max: number;
}
export interface MhRelation {
  targetId: string;
  label: string;
  affinity: BoundedStat;
  notes: string;
  knowsTargetName: boolean;
}
export interface MhCharacter {
  id: string;
  identity: {
    name: string;
    pronouns: string;
    race: string;
    occupation: string;
    affiliation: string;
    title: string;
  };
  hunter: { hasLicense: boolean; hr: number | null };
  presence: {
    status: "present" | "offscene" | "departed";
    location: string;
    lastSeenAt: string;
  };
  appearance: string;
  status: { current: string; health: number | null; stamina: number | null };
  relations: MhRelation[];
  bio: {
    background: string;
    biography: string;
    recentEvents: string[];
    currentMotive: string;
    shortTermGoal: string;
    longTermGoal: string;
  };
  legacy?: Record<string, unknown>;
}
export interface MonsterIndividual {
  id: string;
  name: string;
  speciesId: string;
  individualName: string;
  health: number;
  rage: number;
  fatigue: number;
  parts: Record<string, { durability: number; broken: boolean }>;
  tailSevered: boolean;
  location: string;
  scars: string[];
  territory: string[];
  lastSeenAt: string;
  status: "active" | "fled" | "captured" | "slain" | "unknown";
  legacy?: Record<string, unknown>;
}
export type MonsterState = MonsterIndividual;
export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: number;
  turnId?: string;
  snapshotId?: string;
  branchOptions?: string[];
  turnInput?: TurnInputMetadata;
}
export type AutoContinueMode = "observe" | "seize" | "timeSkip";
export interface AutoContinuePayload {
  mode: AutoContinueMode;
  prompt: string;
  wordCount: number;
  displayLabel: string;
}
export interface TurnInputMetadata {
  content: string;
  autoContinue?: AutoContinuePayload;
}
export interface QuickChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: number;
  speakerId: string;
}
export interface QuickChatConversation {
  id: string;
  kind: "direct" | "group";
  participantIds: string[];
  messages: QuickChatMessage[];
  updatedAt: number;
}
export interface QuickChatState {
  conversations: Record<string, QuickChatConversation>;
  pendingConversationIds: string[];
}
export interface PlotEvolutionState {
  guidance: string;
  guidanceUpdatedTurn: number;
  directorLedger: string;
  currentDirective: string;
  updatedAt: number | null;
}
export interface PendingHuntContext {
  taskTitle: string;
  materials: { key: string; name: string; quantity: number }[];
  reward: GameTask["reward"];
  hrBefore: number;
  hrAfter: number;
}
export interface Archive {
  archiveId: string;
  name: string;
  player: PlayerCharacter;
  characters: Record<string, MhCharacter>;
  monsters: Record<string, MonsterIndividual>;
  itemRegistry: ItemRegistryV1;
  tasks: {
    available: GameTask[];
    activeTaskId: string | null;
    history: GameTask[];
    lastRefreshDay: number;
  };
  messages: ChatMessage[];
  narrativeMemory: {
    summary: string;
    facts: string[];
    lastCompressedTurn: number;
  };
  deepSummary: {
    text: string;
    coveredThroughMessageId: string | null;
    coveredTurns: number;
    updatedAt: number | null;
    revision: number;
  };
  worldEvents: string[];
  scene: { location: string };
  gameTime: { day: number; hour: number };
  pendingCraftingContext: string | null;
  pendingHuntContext: PendingHuntContext | null;
  summaries: string[];
  openingContext: string;
  quickChat: QuickChatState;
  plotEvolution: PlotEvolutionState;
  meta: { createdAt: number; updatedAt: number; version: 4 };
}
